/**
 * Редактор разметки одного диалога на волне (разбор владельца 15.09; модель v2 — раздел R
 * в services/plugins/speech_to_text/TODO.md).
 *
 * Два слоя. ЗВУКОВОЙ («как распознано»): дорожки спикеров (реплика — интервал, внутри неё
 * слова по желанию; у реплики — как она кончилась), неопознанные спикеры отдельной группой,
 * фон (тишина, шум зала, посторонний голос) и звуки (писк, скан, звон, принтер, стук).
 * ЛОГИЧЕСКИЙ слой диалога — не звук: паузы (интервал + что происходит) и переходы между
 * спикерами (точки).
 *
 * Пока человек ничего не сохранял, разметка собрана из прода: реплики расшифровки на кассира и
 * гостя по диалогу судьи, промежутки — фон, писки — звуки, паузы судьи — в слой диалога.
 *
 * Мышь: рамка приближает, двойной клик — вся запись, колесо с Shift двигает. Новое — Cmd/Ctrl +
 * рамка на дорожке (на дорожке диалога — пауза; Cmd/Ctrl + клик там же — переход).
 */
import { Select } from '@/components/ui/select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Flag, HelpCircle, Link2, Loader2, MessageSquare, Minus, Maximize2, Pause, Play, Plus, RotateCcw, Save, Scissors, Trash2, Undo2,
} from 'lucide-react';
import type { DialogueMark, MarkupBody, MarkupEvent, MarkupReply, MarkupSpeaker, TranscriptLine, DialogueLine } from '@/api/conversations';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover } from '@/components/ui/popover';
import type { GlueReport } from './dayStream';
import { ExactPlayer, mixDown } from '@/lib/exactPlayer';
import { Tooltip } from '@/components/ui/tooltip';

export type TSpan = { start: number; end: number; l: TranscriptLine; conv: number };
export type DSpan = { start: number; end: number; l: DialogueLine; conv: number };
export interface MarkupState {
  events: MarkupEvent[]; replies: MarkupReply[]; speakers?: MarkupSpeaker[]; dialogue?: DialogueMark[];
  comment: string; updated_by: string | null; flagged: boolean;
}
export interface AudioLoad { blob: Blob; glue?: Omit<GlueReport, 'blob'>; note?: string }
/** Что размечаем. Время — секунды от нуля audio(). */
export interface EditorSource {
  key: string;
  audio: () => Promise<AudioLoad>;
  transcript: TSpan[];
  dialogue: DSpan[];
  load: () => Promise<MarkupState>;
  save: (b: MarkupBody) => Promise<MarkupState>;
  flag?: (v: boolean) => Promise<boolean>;
  /** Разметка, от которой начать, если своей ещё нет (копия прод-разговора со сдвигом). */
  fallback?: () => Promise<MarkupState | null>;
}

type Cat = 'speech' | 'bg' | 'sound';
const KINDS: { kind: string; label: string; cat: Cat; color: string }[] = [
  { kind: 'реплика', label: 'реплика', cat: 'speech', color: 'bg-sky-500/55' },
  { kind: 'слово', label: 'слово', cat: 'speech', color: 'bg-sky-300/50' },
  { kind: 'речь', label: 'неразборчиво', cat: 'speech', color: 'bg-indigo-300/50' },
  { kind: 'тишина', label: 'тишина', cat: 'bg', color: 'bg-foreground/10' },
  { kind: 'шум', label: 'шум зала', cat: 'bg', color: 'bg-foreground/25' },
  { kind: 'голос', label: 'посторонний голос', cat: 'bg', color: 'bg-violet-400/45' },
  { kind: 'другое', label: 'другое', cat: 'bg', color: 'bg-rose-400/50' },
  { kind: 'писк', label: 'писк оплаты', cat: 'sound', color: 'bg-amber-500/80' },
  { kind: 'скан', label: 'скан карты', cat: 'sound', color: 'bg-amber-300/80' },
  { kind: 'звон', label: 'звон', cat: 'sound', color: 'bg-yellow-400/80' },
  { kind: 'принтер', label: 'принтер чеков', cat: 'sound', color: 'bg-orange-300/80' },
  { kind: 'стук', label: 'стук / тапы по кассе', cat: 'sound', color: 'bg-stone-400/70' },
];
const KIND = Object.fromEntries(KINDS.map((k) => [k.kind, k]));
const catOf = (kind: string): Cat => KIND[kind]?.cat ?? 'bg';
const CAT_LABEL: Record<Cat, string> = { speech: 'речь', bg: 'фон', sound: 'звуки' };
const DEFAULT_KIND: Record<Cat, string> = { speech: 'реплика', bg: 'шум', sound: 'писк' };
const END_KINDS: [string, string][] = [['фраза', 'конец фразы'], ['перебили', 'перебили'], ['продолжает', 'продолжает']];
const GENDERS: [MarkupSpeaker['gender'], string][] = [['', 'неизвестно'], ['м', 'мужчина'], ['ж', 'женщина']];
const AGES: [MarkupSpeaker['age'], string][] = [['', 'неизвестно'], ['ребёнок', 'ребёнок'], ['подросток', 'подросток'], ['молодой', 'молодой'], ['средний', 'средний'], ['старший', 'старший']];
const SPEAKER_ROLES: [MarkupSpeaker['role'], string][] = [['', 'неизвестно'], ['кассир', 'кассир'], ['сотрудник', 'сотрудник'], ['гость', 'гость']];
const DLG_LANE = 'dlg';

const HINTS: { section: string; items: [string, string][] }[] = [
  { section: 'Мышь', items: [
    ['рамка', 'приблизить выделенный участок'],
    ['двойной клик', 'вся запись'],
    ['Shift + колесо', 'сдвинуть по времени (или горизонтальная прокрутка)'],
    ['Ctrl + колесо', 'приблизить у курсора'],
    ['клик по пустому', 'поставить воспроизведение сюда'],
    ['⌘/Ctrl + рамка', 'новое: у спикера — реплика, на фоне — шум, на звуках — писк, в диалоге — пауза'],
    ['⌘/Ctrl + клик в диалоге', 'переход между спикерами'],
    ['клик по событию', 'выбрать и прослушать ровно его'],
    ['Shift + клик', 'выбрать несколько (слова — «сцепить в реплику»)'],
    ['тянуть событие / край', 'сдвинуть / поменять границу; реплика тянет свои слова'],
  ] },
  { section: 'Клавиши', items: [
    ['Пробел', 'пуск / пауза'],
    ['⌘/Ctrl + Z', 'шаг назад'],
    ['Delete', 'удалить выбранное'],
    ['Esc', 'снять выбор'],
  ] },
  { section: 'Дорожки', items: [
    ['спикеры', 'реплики, под ними слова; клик по имени — пол, возраст, роль, удалить'],
    ['неопознанные', 'речь, которую не удалось приписать спикеру'],
    ['фон', 'тишина, шум зала, посторонний голос'],
    ['звуки', 'писк оплаты, скан карты, звон, принтер, стук'],
    ['диалог', 'не звук: паузы (что происходит) и переходы между спикерами'],
    ['серое / оранжевое на волне', 'дыра / наложение между клипами при склейке'],
  ] },
];

const uid = () => Math.random().toString(36).slice(2, 10);
const fmt = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, '0')}`;
const mskTime = (ms: number) =>
  new Date(ms).toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit', second: '2-digit' });

type Model = { speakers: MarkupSpeaker[]; events: MarkupEvent[]; dialogue: DialogueMark[] };
const speaker = (id: string, name: string, p: Partial<MarkupSpeaker> = {}): MarkupSpeaker =>
  ({ id, name, unknown: false, gender: '', age: '', role: '', ...p });

/** Черновик разметки из прода (T11): таскать и править, а не размечать с нуля. Прод сразу
 *  знает кассира и гостя; невнятная речь, не приписанная никому, — неопознанный спикер. */
function fromProd(transcript: TSpan[], dialogue: DSpan[]): Model {
  const speakers: MarkupSpeaker[] = [speaker('s1', 'Спикер 1', { role: 'кассир' }), speaker('s2', 'Спикер 2', { role: 'гость' })];
  const unknownId = 'u1';
  let usedUnknown = false;
  const speakerAt = (a: number, b: number) => {
    let best = 0; let who = '';
    for (const d of dialogue) {
      const ov = Math.min(b, d.end) - Math.max(a, d.start);
      if (ov > best) { best = ov; who = d.l.speaker === 'cashier' ? 's1' : d.l.speaker === 'guest' ? 's2' : ''; }
    }
    if (!who) usedUnknown = true;
    return who || unknownId;
  };
  const gapKind: Record<string, string> = { 'тишина': 'тишина', 'шум': 'шум', 'стук': 'стук' };
  const toneKind: Record<string, string> = { 'скан': 'скан', 'звон': 'звон', 'чек': 'принтер' };
  const events: MarkupEvent[] = transcript.map(({ l, start, end }) => {
    const a = +Math.max(0, start).toFixed(3); const b = +Math.max(a + 0.02, end).toFixed(3);
    // Промежуток «речь» — невнятный голос; «вставка» — короткое слово кого-то из говорящих.
    const kind = l.tone ? (toneKind[l.tone] ?? 'писк')
      : l.gap === 'речь' ? 'речь' : l.gap === 'вставка' ? 'реплика' : l.gap ? (gapKind[l.gap] ?? 'другое') : 'реплика';
    const speech = catOf(kind) === 'speech';
    return { id: uid(), kind, start: a, end: b, text: l.tone || l.gap ? '' : (l.text || '').trim(),
      speaker: speech ? speakerAt(a, b) : '', reply: '', end_kind: '', comment: '' };
  });
  if (usedUnknown) speakers.push(speaker(unknownId, 'Неопознанный 1', { unknown: true }));
  const marks: DialogueMark[] = dialogue.filter((d) => d.l.speaker === 'pause').map((d) => ({
    id: uid(), kind: 'пауза', start: +d.start.toFixed(3), end: +d.end.toFixed(3), text: (d.l.text || '').trim(), from: '', to: '', sub: '',
  }));
  return { speakers, events, dialogue: marks };
}

/** Раскладка по под-дорожкам: пересекающиеся — на разные. */
function stack<T extends { start: number; end: number }>(items: T[]): { item: T; row: number }[] {
  const ends: number[] = [];
  return [...items].sort((a, b) => a.start - b.start).map((item) => {
    let row = ends.findIndex((e) => e <= item.start + 1e-3);
    if (row < 0) { row = ends.length; ends.push(item.end); } else ends[row] = item.end;
    return { item, row };
  });
}

// --- спектр: БПФ по степени двойки
function fft(re: Float32Array, im: Float32Array) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { [re[i], re[j]] = [re[j], re[i]]; [im[i], im[j]] = [im[j], im[i]]; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len; const wr = Math.cos(ang); const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1; let ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const ur = re[i + k]; const ui = im[i + k];
        const vr = re[i + k + len / 2] * cr - im[i + k + len / 2] * ci;
        const vi = re[i + k + len / 2] * ci + im[i + k + len / 2] * cr;
        re[i + k] = ur + vr; im[i + k] = ui + vi; re[i + k + len / 2] = ur - vr; im[i + k + len / 2] = ui - vi;
        const nr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = nr;
      }
    }
  }
}

/** Спектрограмма 0–8 кГц картинкой: столбец — кадр, строка — полоса частот. */
function spectrogram(x: Float32Array, sr: number): { canvas: HTMLCanvasElement; fps: number } {
  const n = 512; const hop = Math.max(160, Math.ceil(x.length / 12000)); const rows = 128;
  const frames = Math.max(1, Math.floor((x.length - n) / hop));
  const win = new Float32Array(n).map((_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1)));
  const maxBin = Math.min(n / 2, Math.floor((8000 / (sr / 2)) * (n / 2)));
  const db = new Float32Array(frames * rows);
  const re = new Float32Array(n); const im = new Float32Array(n);
  for (let f = 0; f < frames; f++) {
    for (let i = 0; i < n; i++) { re[i] = x[f * hop + i] * win[i]; im[i] = 0; }
    fft(re, im);
    for (let r = 0; r < rows; r++) {
      const b0 = Math.floor((r / rows) * maxBin); const b1 = Math.max(b0 + 1, Math.floor(((r + 1) / rows) * maxBin));
      let m = 0;
      for (let b = b0; b < b1; b++) m = Math.max(m, re[b] * re[b] + im[b] * im[b]);
      db[f * rows + r] = 10 * Math.log10(m + 1e-12);
    }
  }
  const sorted = Float32Array.from(db).sort();
  // Контраст по перцентилям: низ спектра всегда громче, и от 30-го перцентиля картинка тонула в жёлтом.
  const lo = sorted[Math.floor(sorted.length * 0.55)]; const hi = sorted[Math.floor(sorted.length * 0.998)];
  const canvas = document.createElement('canvas');
  canvas.width = frames; canvas.height = rows;
  const g = canvas.getContext('2d');
  if (g) {
    const img = g.createImageData(frames, rows);
    for (let f = 0; f < frames; f++) {
      for (let r = 0; r < rows; r++) {
        const v = Math.max(0, Math.min(1, (db[f * rows + r] - lo) / (hi - lo + 1e-9))) ** 1.3;
        const p = ((rows - 1 - r) * frames + f) * 4;
        // тёмно-фиолетовый → оранжевый → светло-жёлтый
        img.data[p] = Math.round(20 + 235 * Math.min(1, v * 1.4));
        img.data[p + 1] = Math.round(10 + 230 * Math.max(0, v - 0.35) / 0.65);
        img.data[p + 2] = Math.round(60 + 90 * Math.sin(Math.PI * Math.min(1, v * 1.2)) - 40 * v);
        img.data[p + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
  }
  return { canvas, fps: sr / hop };
}

const HEAD_W = 128;
const ROW_H = 18;
const WAVE_H = 72;
const SPEC_H = 64;
const RULER_H = 16;

export default function MarkupEditor({ source, onChanged, frameStartMs }: { source: EditorSource; onChanged: () => void; frameStartMs?: number }) {
  const { data: saved, mutate: mutateSaved } = useSWR(['markup', source.key], () => source.load(), { revalidateOnFocus: false });
  const [events, setEvents] = useState<MarkupEvent[]>([]);
  const [speakers, setSpeakers] = useState<MarkupSpeaker[]>([]);
  const [dialogue, setDialogue] = useState<DialogueMark[]>([]);
  const [comment, setComment] = useState('');
  const [baseline, setBaseline] = useState<string>('');
  const [origin, setOrigin] = useState<'saved' | 'prod' | 'copy'>('saved');
  type Snap = Model & { comment: string };
  const [history, setHistory] = useState<Snap[]>([]);
  const [sel, setSel] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [speakerEdit, setSpeakerEdit] = useState<string | null>(null);
  const helpRef = useRef<HTMLButtonElement>(null);
  const commentRef = useRef<HTMLButtonElement>(null);

  const snapshot = (m: Snap) => JSON.stringify(m);
  const current: Snap = { speakers, events, dialogue, comment };
  const srcRef = useRef(source); srcRef.current = source;
  useEffect(() => {
    if (!saved) return;
    let alive = true;
    const apply = (m: Snap, o: 'saved' | 'prod' | 'copy') => {
      setSpeakers(m.speakers); setEvents(m.events); setDialogue(m.dialogue); setComment(m.comment);
      setBaseline(snapshot(m)); setOrigin(o); setHistory([]); setSel([]);
    };
    const asModel = (st: MarkupState): Snap => ({ speakers: st.speakers ?? [], events: st.events, dialogue: st.dialogue ?? [], comment: st.comment });
    if (saved.events.length || saved.updated_by) { apply(asModel(saved), 'saved'); return; }
    void (async () => {
      const fb = srcRef.current.fallback ? await srcRef.current.fallback().catch(() => null) : null;
      if (!alive) return;
      if (fb && fb.events.length) apply(asModel(fb), 'copy');
      else apply({ ...fromProd(srcRef.current.transcript, srcRef.current.dialogue), comment: saved.comment }, 'prod');
    })();
    return () => { alive = false; };
  }, [saved]);
  const dirty = snapshot(current) !== baseline;

  /** Любая правка — через commit: прошлое состояние уходит в историю (Ctrl/Cmd+Z). */
  const commit = useCallback((fn: (m: Model) => Partial<Model>) => {
    setHistory((h) => [...h.slice(-199), { speakers, events, dialogue, comment }]);
    const r = fn({ speakers, events, dialogue });
    if (r.speakers) setSpeakers(r.speakers);
    if (r.events) setEvents(r.events);
    if (r.dialogue) setDialogue(r.dialogue);
  }, [speakers, events, dialogue, comment]);
  const restore = (m: Snap) => { setSpeakers(m.speakers); setEvents(m.events); setDialogue(m.dialogue); setComment(m.comment); };
  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    restore(last); setHistory((h) => h.slice(0, -1));
  };
  const revert = () => {
    try {
      const b = JSON.parse(baseline) as Snap;
      setHistory((h) => [...h, current]);
      restore(b); setSel([]);
    } catch { /* нечего возвращать */ }
  };

  // ---- звук
  const [audioErr, setAudioErr] = useState('');
  const [glue, setGlue] = useState<AudioLoad['glue'] | null>(null);
  const [note, setNote] = useState('');
  const [samples, setSamples] = useState<{ x: Float32Array; sr: number } | null>(null);
  const [spec, setSpec] = useState<{ canvas: HTMLCanvasElement; fps: number } | null>(null);
  useEffect(() => {
    let alive = true;
    setAudioErr(''); setGlue(null); setSamples(null); setSpec(null); setNote('');
    srcRef.current.audio()
      .then(async (r) => {
        if (!alive) return;
        setGlue(r.glue ?? null); setNote(r.note ?? '');
        const ctx = new OfflineAudioContext(1, 1, 16000);
        const buf = await ctx.decodeAudioData(await r.blob.arrayBuffer());
        if (!alive) return;
        const x = mixDown(buf);
        setSamples({ x, sr: buf.sampleRate });
        // спектр считается секунду-другую на длинной записи — не держим им первый кадр
        window.setTimeout(() => { if (alive) setSpec(spectrogram(x, buf.sampleRate)); }, 30);
      })
      .catch((e: unknown) => alive && setAudioErr(e instanceof Error && e.message && !e.message.startsWith('Request') ? e.message : 'Запись не загрузилась.'));
    return () => { alive = false; };
  }, [source.key]);
  const dur = samples ? samples.x.length / samples.sr : 0;

  // ---- вид: окно [v0, v1] секунд
  const [view, setView] = useState<[number, number]>([0, 1]);
  useEffect(() => { if (dur) setView([0, dur]); }, [dur]);
  const [v0, v1] = view;
  const zoomed = dur > 0 && (v0 > 0.01 || v1 < dur - 0.01);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [W, setW] = useState(900);
  useEffect(() => {
    const el = boxRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(300, el.clientWidth - HEAD_W)));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  const xOf = useCallback((t: number) => ((t - v0) / Math.max(1e-6, v1 - v0)) * W, [v0, v1, W]);
  const tOf = useCallback((px: number) => v0 + (px / W) * (v1 - v0), [v0, v1, W]);
  const setViewClamped = useCallback((a: number, b: number) => {
    const span = Math.max(0.2, Math.min(dur || 1, b - a));
    let s = a; if (s < 0) s = 0; if (s + span > dur) s = Math.max(0, dur - span);
    setView([s, s + span]);
  }, [dur]);
  const zoomOut = () => {
    const ev = events.filter((e) => sel.includes(e.id));
    const c = ev.length ? (Math.min(...ev.map((e) => e.start)) + Math.max(...ev.map((e) => e.end))) / 2 : (v0 + v1) / 2;
    const span = (v1 - v0) * 2;
    setViewClamped(c - span / 2, c + span / 2);
  };

  // ---- воспроизведение: Web Audio по декодированным отсчётам (см. lib/exactPlayer.ts)
  const player = useMemo(() => (samples ? new ExactPlayer(samples.x, samples.sr) : null), [samples]);
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const timeRef = useRef<HTMLSpanElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [following, setFollowing] = useState(false);
  const viewRef = useRef(view); viewRef.current = view;
  const place = useCallback((t: number) => {
    const [a, b] = viewRef.current;
    if (cursorRef.current) {
      cursorRef.current.style.left = `${HEAD_W + ((t - a) / Math.max(1e-6, b - a)) * W}px`;
      cursorRef.current.style.display = t >= a && t <= b ? 'block' : 'none';
    }
    if (timeRef.current) timeRef.current.textContent = `${fmt(t)} / ${fmt(dur)}${frameStartMs != null ? ` · ${mskTime(frameStartMs + t * 1000)}` : ''}`;
  }, [W, dur, frameStartMs]);
  useEffect(() => {
    if (!player) return;
    player.onStop = (t) => { setPlaying(false); place(t); };
    return () => { player.onStop = undefined; player.close(); };
  }, [player, place]);
  useEffect(() => { place(player?.position() ?? 0); }, [place, view, player]);
  useEffect(() => {
    if (!playing || !player) return;
    let raf = 0;
    const tick = () => {
      const t = player.position();
      const [a, b] = viewRef.current;
      // Вид едет за звуком только при свободном проигрывании: кусок играется там, где его нажали.
      if (following && (t > b || t < a) && b - a < dur - 0.01) setViewClamped(t - (b - a) * 0.1, t + (b - a) * 0.9);
      place(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, player, following, place, dur, setViewClamped]);
  const playRange = (a: number, b: number | null) => {
    if (!player) return;
    player.play(a, b); setFollowing(b == null); setPlaying(true); place(a);
  };
  const seek = (t: number) => { if (!player) return; player.seek(t); place(player.position()); };
  const toggle = () => {
    if (!player) return;
    if (player.playing) player.pause();
    else { player.play(player.position() >= dur - 0.02 ? 0 : player.position(), null); setFollowing(true); setPlaying(true); }
  };

  // ---- дорожки
  type LaneDef = { key: string; kind: 'lane' | 'head'; cat: Cat | 'dlg'; speaker: string; title: string; unknown?: boolean };
  const laneDefs = useMemo<LaneDef[]>(() => {
    const known = speakers.filter((q) => !q.unknown); const unknown = speakers.filter((q) => q.unknown);
    return [
      { key: 'head:known', kind: 'head', cat: 'speech', speaker: '', title: 'спикеры' },
      ...known.map((q) => ({ key: `sp:${q.id}`, kind: 'lane' as const, cat: 'speech' as Cat, speaker: q.id, title: q.name })),
      { key: 'head:unknown', kind: 'head', cat: 'speech', speaker: '', title: 'неопознанные', unknown: true },
      ...unknown.map((q) => ({ key: `sp:${q.id}`, kind: 'lane' as const, cat: 'speech' as Cat, speaker: q.id, title: q.name })),
      { key: 'bg', kind: 'lane', cat: 'bg', speaker: '', title: 'фон' },
      { key: 'sound', kind: 'lane', cat: 'sound', speaker: '', title: 'звуки' },
      { key: DLG_LANE, kind: 'lane', cat: 'dlg', speaker: '', title: 'диалог' },
    ];
  }, [speakers]);
  const speakerIds = useMemo(() => new Set(speakers.map((q) => q.id)), [speakers]);
  const laneKey = useCallback((e: MarkupEvent) => {
    const c = catOf(e.kind);
    if (c !== 'speech') return c;
    return speakerIds.has(e.speaker) ? `sp:${e.speaker}` : `sp:${speakers[0]?.id ?? ''}`;
  }, [speakerIds, speakers]);
  type Placed = { item: MarkupEvent | DialogueMark; row: number; layer: 'ev' | 'dlg' };
  const layout = useMemo(() => {
    let y = 0;
    const out: (LaneDef & { top: number; h: number; items: Placed[] })[] = [];
    for (const L of laneDefs) {
      if (L.kind === 'head') { out.push({ ...L, top: y, h: 16, items: [] }); y += 16; continue; }
      let items: Placed[];
      if (L.cat === 'dlg') {
        items = stack(dialogue).map(({ item, row }) => ({ item, row, layer: 'dlg' as const }));
      } else {
        const mine = events.filter((e) => laneKey(e) === L.key);
        // Реплики и неразборчивое — сверху, слова — под ними: так видно, из чего собрана реплика.
        const top = stack(mine.filter((e) => e.kind !== 'слово'));
        const base = Math.max(0, ...top.map((i) => i.row + 1));
        const words = stack(mine.filter((e) => e.kind === 'слово')).map(({ item, row }) => ({ item, row: row + base }));
        items = [...top, ...words].map((q) => ({ ...q, layer: 'ev' as const }));
      }
      const rows = Math.max(1, ...items.map((i) => i.row + 1));
      const h = rows * ROW_H + 6;
      out.push({ ...L, top: y, h, items }); y += h;
    }
    return { lanes: out, height: y };
  }, [laneDefs, events, dialogue, laneKey]);

  // ---- правка событий
  const addEvent = (a: number, b: number, lane: { cat: Cat; speaker: string }) => {
    const ev: MarkupEvent = { id: uid(), kind: DEFAULT_KIND[lane.cat], start: +a.toFixed(3), end: +b.toFixed(3), text: '',
      speaker: lane.cat === 'speech' ? lane.speaker : '', reply: '', end_kind: '', comment: '' };
    commit(({ events: xs }) => ({ events: [...xs, ev] })); setSel([ev.id]);
  };
  /** Кто говорил последним до t и кто первым после — для нового перехода. */
  const neighbours = (t: number) => {
    const speech = events.filter((e) => catOf(e.kind) === 'speech' && e.kind !== 'слово');
    const before = [...speech].filter((e) => e.start <= t).sort((p, q) => q.end - p.end)[0];
    const after = [...speech].filter((e) => e.start >= t).sort((p, q) => p.start - q.start)[0];
    return { from: before?.speaker ?? '', to: after?.speaker ?? '' };
  };
  const addDialogue = (kind: DialogueMark['kind'], a: number, b: number) => {
    const n = kind === 'переход' ? neighbours(a) : { from: '', to: '' };
    const d: DialogueMark = { id: uid(), kind, start: +a.toFixed(3), end: +(kind === 'переход' ? a : b).toFixed(3), text: '',
      from: n.from, to: n.to, sub: kind === 'переход' ? 'смена' : '' };
    commit(({ dialogue: ds }) => ({ dialogue: [...ds, d] })); setSel([d.id]);
  };
  const patchEvent = (id: string, p: Partial<MarkupEvent>) => commit(({ events: xs }) => ({ events: xs.map((e) => (e.id === id ? { ...e, ...p } : e)) }));
  const patchDialogue = (id: string, p: Partial<DialogueMark>) => commit(({ dialogue: ds }) => ({ dialogue: ds.map((d) => (d.id === id ? { ...d, ...p } : d)) }));
  const removeSelected = (ids: string[]) => {
    commit(({ events: xs, dialogue: ds }) => ({
      // Удалили реплику — её слова остаются, просто без реплики.
      events: xs.filter((e) => !ids.includes(e.id)).map((e) => (e.reply && ids.includes(e.reply) ? { ...e, reply: '' } : e)),
      dialogue: ds.filter((d) => !ids.includes(d.id)),
    }));
    setSel([]);
  };
  /** Сцепить выбранные слова (и реплики) одного спикера в реплику. */
  const linkWords = () => {
    const evs = events.filter((e) => sel.includes(e.id) && (e.kind === 'слово' || e.kind === 'реплика')).sort((a, b) => a.start - b.start);
    if (evs.length < 2) return;
    const who = evs[0].speaker;
    const ids = new Set(evs.map((e) => e.id));
    const span = { start: evs[0].start, end: Math.max(...evs.map((e) => e.end)), text: evs.map((e) => e.text).filter(Boolean).join(' ') };
    // Все слова одной реплики сцепили заново — это та же реплика: обновляем её, а не плодим вторую.
    const olds = [...new Set(evs.map((e) => e.reply).filter(Boolean))] as string[];
    const same = olds.length === 1 && evs.every((e) => e.reply === olds[0]) && wordsOf(olds[0]).every((w) => ids.has(w.id)) ? olds[0] : null;
    const r: MarkupEvent = same
      ? { ...(events.find((e) => e.id === same) as MarkupEvent), ...span }
      : { id: uid(), kind: 'реплика', ...span, speaker: who, reply: '', end_kind: '', comment: '' };
    commit(({ events: xs }) => ({
      events: [
        ...xs.filter((e) => e.id !== r.id).map((e) => (ids.has(e.id) ? { ...e, kind: 'слово', speaker: who, reply: r.id, end_kind: '' } : e)),
        r,
      ],
    }));
    setSel([r.id]);
  };
  /** Разбить реплику на слова: по её тексту, время — пропорционально длине слов. */
  const splitReply = (r: MarkupEvent) => {
    const tokens = (r.text || '').split(/\s+/).filter(Boolean);
    if (!tokens.length) return;
    const total = tokens.reduce((q, w) => q + w.length, 0);
    let t = r.start;
    const words = tokens.map((w) => {
      const len = ((r.end - r.start) * w.length) / total;
      const ev: MarkupEvent = { id: uid(), kind: 'слово', start: +t.toFixed(3), end: +(t + len).toFixed(3), text: w,
        speaker: r.speaker, reply: r.id, end_kind: '', comment: '' };
      t += len; return ev;
    });
    commit(({ events: xs }) => ({ events: [...xs, ...words] }));
  };
  const addSpeaker = (unknown: boolean) => {
    const n = speakers.filter((q) => q.unknown === unknown).length + 1;
    const q = speaker(uid(), unknown ? `Неопознанный ${n}` : `Спикер ${n}`, { unknown });
    commit(({ speakers: ss }) => ({ speakers: [...ss, q] }));
  };
  const patchSpeaker = (id: string, p: Partial<MarkupSpeaker>) =>
    commit(({ speakers: ss }) => ({ speakers: ss.map((q) => (q.id === id ? { ...q, ...p } : q)) }));
  /** Удалить спикера: его речь — другому (moveTo) или удалить вместе с ним. */
  const deleteSpeaker = (id: string, moveTo: string | null) => {
    commit(({ speakers: ss, events: xs, dialogue: ds }) => {
      const gone = new Set(xs.filter((e) => catOf(e.kind) === 'speech' && e.speaker === id).map((e) => e.id));
      return {
        speakers: ss.filter((q) => q.id !== id),
        events: moveTo
          ? xs.map((e) => (gone.has(e.id) ? { ...e, speaker: moveTo } : e))
          : xs.filter((e) => !gone.has(e.id)).map((e) => (e.reply && gone.has(e.reply) ? { ...e, reply: '' } : e)),
        dialogue: ds.map((d) => ({ ...d, from: d.from === id ? (moveTo ?? '') : d.from, to: d.to === id ? (moveTo ?? '') : d.to })),
      };
    });
    setSpeakerEdit(null);
  };
  const nameOf = (id: string) => speakers.find((q) => q.id === id)?.name ?? '—';

  // ---- мышь на шкале
  const timeline = useRef<HTMLDivElement | null>(null);
  const [marquee, setMarquee] = useState<{ x0: number; x1: number; y0: number; y1: number; create: boolean } | null>(null);
  const gesture = useRef<{ mode: 'marquee' | 'pan'; x0: number; y0: number; v: [number, number]; lane: string | null; create: boolean } | null>(null);
  const evDrag = useRef<{
    id: string; layer: 'ev' | 'dlg'; mode: 'l' | 'r' | 'm'; x0: number; a: number; b: number; moved: boolean;
    /** Слова реплики с исходным временем — едут вместе с ней. */
    kids: { id: string; a: number; b: number }[];
  } | null>(null);
  const local = (e: { clientX: number; clientY: number }) => {
    const r = timeline.current?.getBoundingClientRect();
    return { x: e.clientX - (r?.left ?? 0) - HEAD_W, y: e.clientY - (r?.top ?? 0) };
  };
  const onDown = (e: React.PointerEvent) => {
    if (e.button === 2 || !dur) return;
    const { x, y } = local(e);
    if (x < 0) return;
    const laneEl = (e.target as HTMLElement).closest('[data-lane]') as HTMLElement | null;
    const pan = e.button === 1 || e.altKey;
    gesture.current = { mode: pan ? 'pan' : 'marquee', x0: x, y0: y, v: view, lane: laneEl?.dataset.lane ?? null, create: e.metaKey || e.ctrlKey };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    if (!pan) setMarquee({ x0: x, x1: x, y0: y, y1: y, create: e.metaKey || e.ctrlKey });
  };
  const onMove = (e: React.PointerEvent) => {
    const d = evDrag.current;
    if (d) {
      const dt = ((e.clientX - d.x0) / W) * (v1 - v0);
      if (!d.moved && Math.abs(e.clientX - d.x0) < 3) return;
      if (!d.moved) { d.moved = true; setHistory((h) => [...h.slice(-199), current]); }
      const shift = <T extends { id: string; start: number; end: number }>(ev: T): T => {
        if (ev.id === d.id) {
          if (d.mode === 'l') return { ...ev, start: +Math.max(0, Math.min(d.b - 0.02, d.a + dt)).toFixed(3) };
          if (d.mode === 'r') return { ...ev, end: +Math.min(dur, Math.max(d.a + 0.02, d.b + dt)).toFixed(3) };
          const len = d.b - d.a; const a = Math.max(0, Math.min(dur - len, d.a + dt));
          return { ...ev, start: +a.toFixed(3), end: +(a + len).toFixed(3) };
        }
        const kid = d.mode === 'm' ? d.kids.find((k) => k.id === ev.id) : undefined;
        if (!kid) return ev;
        const moved = Math.max(0, Math.min(dur - (d.b - d.a), d.a + dt)) - d.a;
        return { ...ev, start: +(kid.a + moved).toFixed(3), end: +(kid.b + moved).toFixed(3) };
      };
      if (d.layer === 'dlg') setDialogue((xs) => xs.map((q) => {
        const r = shift(q);
        return r.kind === 'переход' ? { ...r, end: r.start } : r;
      }));
      else setEvents((xs) => xs.map(shift));
      return;
    }
    const g = gesture.current; if (!g) return;
    const { x, y } = local(e);
    if (g.mode === 'pan') {
      const dt = ((x - g.x0) / W) * (g.v[1] - g.v[0]);
      setViewClamped(g.v[0] - dt, g.v[1] - dt);
    } else setMarquee((m) => (m ? { ...m, x1: x, y1: y } : m));
  };
  const onUp = (e: React.PointerEvent) => {
    const d = evDrag.current;
    if (d) {
      evDrag.current = null;
      if (!d.moved) {
        const id = d.id;
        setSel((s) => (e.shiftKey ? (s.includes(id) ? s.filter((q) => q !== id) : [...s, id]) : [id]));
        const it = d.layer === 'dlg' ? dialogue.find((q) => q.id === id) : events.find((q) => q.id === id);
        if (it && !e.shiftKey) {
          if (it.end - it.start > 0.01) playRange(it.start, it.end); else seek(it.start);
        }
      }
      return;
    }
    const g = gesture.current; gesture.current = null;
    const m = marquee; setMarquee(null);
    if (!g || g.mode === 'pan' || !m) return;
    const a = tOf(Math.min(m.x0, m.x1)); const b = tOf(Math.max(m.x0, m.x1));
    const tiny = Math.abs(m.x1 - m.x0) < 4;
    if (g.create && g.lane === DLG_LANE) {
      if (tiny) addDialogue('переход', Math.max(0, a), a);
      else addDialogue('пауза', Math.max(0, a), Math.min(dur, b));
      return;
    }
    if (tiny) { seek(a); if (!e.shiftKey) setSel([]); return; }
    if (g.create) {
      const L = layout.lanes.find((q) => q.key === g.lane && q.kind === 'lane');
      if (L && L.cat !== 'dlg') addEvent(Math.max(0, a), Math.min(dur, b), { cat: L.cat, speaker: L.speaker });
      else if (speakers[0]) addEvent(Math.max(0, a), Math.min(dur, b), { cat: 'speech', speaker: speakers[0].id });
    } else setViewClamped(a, b);
  };
  useEffect(() => {
    const el = timeline.current; if (!el) return;
    const wheel = (e: WheelEvent) => {
      if (!dur) return;
      const [a, b] = viewRef.current;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const r = el.getBoundingClientRect(); const px = e.clientX - r.left - HEAD_W;
        const c = a + (Math.max(0, px) / W) * (b - a); const k = Math.exp(e.deltaY * 0.01);
        setViewClamped(c - (c - a) * k, c + (b - c) * k);
      } else if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        const dt = ((e.shiftKey ? e.deltaY : e.deltaX) / W) * (b - a);
        setViewClamped(a + dt, b + dt);
      }
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  }, [W, dur, setViewClamped]);

  // ---- волна и спектр под текущее окно
  const waveCanvas = useRef<HTMLCanvasElement | null>(null);
  const specCanvas = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const c = waveCanvas.current; if (!c || !samples) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = W * dpr; c.height = WAVE_H * dpr;
    const g = c.getContext('2d'); if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, W, WAVE_H);
    const { x, sr } = samples; const i0 = Math.floor(v0 * sr); const per = ((v1 - v0) * sr) / W;
    let peak = 1e-3; for (let i = 0; i < x.length; i += 64) peak = Math.max(peak, Math.abs(x[i]));
    g.fillStyle = '#94a3b8';
    const step = Math.max(1, Math.floor(per / 48));
    for (let px = 0; px < W; px++) {
      const s0 = i0 + Math.floor(px * per); const s1 = Math.min(x.length, i0 + Math.floor((px + 1) * per) + 1);
      let mn = 0; let mx = 0;
      for (let i = s0; i < s1; i += step) { const v = x[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
      const yTop = WAVE_H / 2 - (mx / peak) * (WAVE_H / 2 - 2); const yBot = WAVE_H / 2 - (mn / peak) * (WAVE_H / 2 - 2);
      g.fillRect(px, yTop, 1, Math.max(1, yBot - yTop));
    }
  }, [samples, v0, v1, W]);
  useEffect(() => {
    const c = specCanvas.current; if (!c || !spec) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = W * dpr; c.height = SPEC_H * dpr;
    const g = c.getContext('2d'); if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0); g.imageSmoothingEnabled = true;
    const sx = v0 * spec.fps; const sw = Math.max(1, (v1 - v0) * spec.fps);
    g.drawImage(spec.canvas, sx, 0, sw, spec.canvas.height, 0, 0, W, SPEC_H);
    g.fillStyle = 'rgba(255,255,255,0.7)'; g.font = '9px sans-serif';
    for (const hz of [2000, 4000, 6000]) { const y = SPEC_H - (hz / 8000) * SPEC_H; g.fillRect(0, y, 4, 1); g.fillText(`${hz / 1000}к`, 6, y + 3); }
  }, [spec, v0, v1, W]);

  // ---- линейка
  const ticks = useMemo(() => {
    const span = v1 - v0; const steps = [0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300];
    const step = steps.find((s) => (s / span) * W >= 70) ?? 600;
    const out: number[] = []; for (let t = Math.ceil(v0 / step) * step; t <= v1; t += step) out.push(+t.toFixed(3));
    return out;
  }, [v0, v1, W]);

  // ---- клавиши
  const onKey = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (e.key === ' ') { e.preventDefault(); toggle(); }
    else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); }
    else if ((e.key === 'Delete' || e.key === 'Backspace') && sel.length) { e.preventDefault(); removeSelected(sel); }
    else if (e.key === 'Escape') setSel([]);
  };

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await source.save({ speakers, events, dialogue, replies: [], comment });
      await mutateSaved(r, { revalidate: false }); setMsg('сохранено'); onChanged();
    } catch { setMsg('не сохранилось'); } finally { setBusy(false); }
  };
  const flag = async (v: boolean) => {
    if (!source.flag || !saved) return;
    const flagged = await source.flag(v);
    await mutateSaved({ ...saved, flagged }, { revalidate: false }); onChanged();
  };

  const one = sel.length === 1 ? events.find((e) => e.id === sel[0]) : undefined;
  const oneDlg = sel.length === 1 ? dialogue.find((d) => d.id === sel[0]) : undefined;
  const wordsOf = (id: string) => events.filter((e) => e.reply === id);
  const selWords = events.filter((e) => sel.includes(e.id) && (e.kind === 'слово' || e.kind === 'реплика'));
  const canLink = sel.length > 1 && selWords.length === sel.length && new Set(selWords.map((e) => e.speaker)).size === 1;
  const status = msg && !dirty ? msg
    : dirty ? 'есть несохранённые правки'
      : origin === 'prod' ? 'черновик из прода, человек ещё не сохранял'
        : origin === 'copy' ? 'перенесено из разметки прод-разговора, ещё не сохранено'
          : saved?.updated_by ? `сохранил ${saved.updated_by}` : '';
  const editing = speakerEdit ? speakers.find((q) => q.id === speakerEdit) : undefined;

  return (
    <div tabIndex={0} onKeyDown={onKey} className="space-y-2 bg-muted/20 px-3 pb-4 pt-2 outline-none">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Button size="sm" variant="outline" onClick={toggle} disabled={!player}>
          {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
          <span ref={timeRef} className="tabular-nums">0:00.0</span>
        </Button>
        {zoomed && (
          <>
            <Button size="sm" variant="outline" onClick={() => setView([0, dur])} title="Вся запись (двойной клик)"><Maximize2 className="size-3.5" /> вся запись</Button>
            <Button size="sm" variant="outline" onClick={zoomOut} title="Отдалить вокруг выбранного"><Minus className="size-3.5" /> отдалить</Button>
          </>
        )}
        {source.flag && (
          <Button size="sm" variant="ghost" onClick={() => void flag(!saved?.flagged)} title={saved?.flagged ? 'Снять пометку' : 'Пометить для разметки'}>
            <Flag className={`size-3.5 ${saved?.flagged ? 'fill-amber-500 text-amber-500' : ''}`} />
          </Button>
        )}
        <Button ref={commentRef} size="sm" variant="ghost" onClick={() => setCommentOpen((v) => !v)} title="Комментарий к разговору">
          <MessageSquare className={`size-3.5 ${comment ? 'fill-primary/20 text-primary' : ''}`} />
        </Button>
        <Popover open={commentOpen} onOpenChange={setCommentOpen} triggerRef={commentRef} side="bottom" align="start">
          <div className="w-[26rem] max-w-[90vw] p-2">
            <textarea autoFocus className="min-h-24 w-full rounded border bg-background px-2 py-1 text-sm" placeholder="Что не так в разборе этого разговора"
              value={comment} onChange={(e) => { const v = e.target.value; if (!history.length || history[history.length - 1].comment !== comment) setHistory((h) => [...h.slice(-199), current]); setComment(v); }} />
          </div>
        </Popover>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-muted-foreground">{status}</span>
          <span className="tabular-nums text-muted-foreground">событий {events.length} · реплик {events.filter((e) => e.kind === 'реплика').length} · в диалоге {dialogue.length}</span>
          <Button size="sm" variant="outline" onClick={undo} disabled={!history.length} title="Шаг назад (⌘/Ctrl+Z)"><Undo2 className="size-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={revert} disabled={!dirty} title="Вернуть как было при открытии"><RotateCcw className="size-3.5" /> отменить правки</Button>
          <Button size="sm" onClick={() => void save()} disabled={busy || (!dirty && origin === 'saved')}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />} сохранить
          </Button>
          <Button ref={helpRef} size="sm" variant="ghost" onClick={() => setHelpOpen((v) => !v)} aria-label="Подсказки"><HelpCircle className="size-4 text-muted-foreground" /></Button>
        </span>
        <Popover open={helpOpen} onOpenChange={setHelpOpen} triggerRef={helpRef} side="bottom" align="end">
          <div className="w-[26rem] max-w-[90vw] divide-y divide-border">
            {HINTS.map((group) => (
              <section key={group.section} className="px-4 py-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{group.section}</div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                  {group.items.map(([key, desc]) => (
                    <div key={key} className="contents">
                      <dt className="inline-flex items-center justify-self-start whitespace-nowrap rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] font-medium text-foreground">{key}</dt>
                      <dd className="self-center leading-snug text-muted-foreground">{desc}</dd>
                    </div>
                  ))}
                </dl>
                {group.section === 'Дорожки' && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {KINDS.map((k) => <span key={k.kind} className="inline-flex items-center gap-1 rounded border px-1 text-[10px]"><span className={`inline-block size-2 rounded-sm ${k.color}`} />{k.label}</span>)}
                  </div>
                )}
              </section>
            ))}
          </div>
        </Popover>
      </div>
      {audioErr && <div className="text-sm text-muted-foreground">{audioErr}</div>}
      {(glue || note) && (
        <div className="text-xs text-muted-foreground">
          {note}
          {glue && <>{note ? 'клипов' : 'собрано из клипов'}: {glue.clips}
            {glue.failed.length > 0 && <span className="text-rose-600"> · не скачались {glue.failed.join(', ')}</span>}
            {' · '}дыр записи {glue.holes.length}{glue.holes.length > 0 && ` (${glue.holes.reduce((q, h) => q + h.dur, 0).toFixed(1)} с — звука там нет, запись не шла)`}
            {' · '}наложений {glue.overlaps.length}</>}
        </div>
      )}
      {!audioErr && !samples && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> запись загружается</div>}

      <div ref={boxRef} className="rounded border bg-card">
        <div ref={timeline} className="relative select-none" style={{ touchAction: 'none' }}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
          onDoubleClick={(e) => { if (local(e).x >= 0 && !(e.target as HTMLElement).closest('[data-ev]')) setView([0, dur]); }}>
          {/* линейка */}
          <div className="relative border-b text-[9px] text-muted-foreground" style={{ height: RULER_H, marginLeft: HEAD_W }}>
            {ticks.map((t) => <span key={t} className="absolute top-0 border-l border-border pl-0.5" style={{ left: xOf(t) }}>{fmt(t).replace(/\.0$/, '')}</span>)}
          </div>
          {/* волна */}
          <div className="relative flex border-b" style={{ height: WAVE_H }}>
            <div className="shrink-0 px-1 py-0.5 text-[10px] text-muted-foreground" style={{ width: HEAD_W }}>волна</div>
            <div className="relative overflow-hidden" style={{ width: W }}>
              <canvas ref={waveCanvas} className="pointer-events-none absolute inset-0" style={{ width: W, height: WAVE_H }} />
              {glue?.holes.map((h, i) => (
                <div key={`h${i}`} className="pointer-events-none absolute inset-y-0 flex items-center justify-center overflow-hidden bg-foreground/15 text-[10px] text-muted-foreground" style={{ left: xOf(h.at), width: Math.max(2, xOf(h.at + h.dur) - xOf(h.at)) }}>
                  {xOf(h.at + h.dur) - xOf(h.at) > 60 ? 'нет записи' : ''}
                </div>
              ))}
              {glue?.overlaps.map((h, i) => (
                <div key={`o${i}`} className="pointer-events-none absolute inset-y-0 bg-orange-400/40" style={{ left: xOf(h.at), width: Math.max(2, xOf(h.at + h.dur) - xOf(h.at)) }} />
              ))}
            </div>
          </div>
          {/* частоты (T10) */}
          <div className="relative flex border-b" style={{ height: SPEC_H }}>
            <div className="shrink-0 px-1 py-0.5 text-[10px] leading-tight text-muted-foreground" style={{ width: HEAD_W }}>частоты<br />0–8 кГц</div>
            <div className="relative overflow-hidden bg-black/80" style={{ width: W }}>
              <canvas ref={specCanvas} className="pointer-events-none absolute inset-0" style={{ width: W, height: SPEC_H }} />
              {samples && !spec && <span className="absolute left-2 top-1 text-[10px] text-white/70">спектр считается…</span>}
            </div>
          </div>
          {/* дорожки разметки */}
          <div className="relative" style={{ height: layout.height }}>
            {layout.lanes.map((L, li) => {
              if (L.kind === 'head') {
                return (
                  <div key={L.key} className={`absolute inset-x-0 flex items-center ${li > 0 ? 'border-t-2' : ''} border-border/70 bg-muted/40`}
                    style={{ top: L.top, height: L.h }} onPointerDown={(e) => e.stopPropagation()}>
                    <div className="flex shrink-0 items-center gap-1 px-1 text-[10px] font-medium text-muted-foreground" style={{ width: HEAD_W }}>
                      {L.title}
                      <Tooltip content={L.unknown ? 'Добавить неопознанного' : 'Добавить спикера'}><button type="button" onClick={() => addSpeaker(!!L.unknown)} aria-label={L.unknown ? 'Добавить неопознанного' : 'Добавить спикера'}
                        className="ml-auto inline-flex items-center rounded-[2px] px-1 hover:bg-muted hover:text-foreground">
                        <Plus className="size-3" />
                      </button></Tooltip>
                    </div>
                  </div>
                );
              }
              const prev = layout.lanes[li - 1];
              const groupEdge = prev && (prev.kind === 'head' ? false : prev.cat !== L.cat);
              const q = L.cat === 'speech' ? speakers.find((x) => x.id === L.speaker) : undefined;
              return (
                <div key={L.key} data-lane={L.key} className={`absolute inset-x-0 flex ${groupEdge ? 'border-t-2' : 'border-t'} border-border/70`}
                  style={{ top: L.top, height: L.h }}>
                  <div className="z-10 flex shrink-0 items-center gap-1 bg-card px-1 text-[10px]" style={{ width: HEAD_W }}
                    onPointerDown={(e) => e.stopPropagation()}>
                    {q ? (
                      <Tooltip content="Пол, возраст, роль, удалить"><button type="button" onClick={() => setSpeakerEdit(q.id)}
                        className="w-full truncate rounded-[2px] px-1 py-0.5 text-left hover:bg-muted">
                        {q.name}
                        {(q.role || q.gender || q.age) && (
                          <span className="text-muted-foreground"> · {[q.role, q.gender, q.age].filter(Boolean).join(', ')}</span>
                        )}
                      </button></Tooltip>
                    ) : <span className="px-1 text-muted-foreground">{L.title}</span>}
                  </div>
                  <div className="relative overflow-hidden" style={{ width: W }}>
                    {L.items.map(({ item, row, layer }) => {
                      const on = sel.includes(item.id);
                      const startDrag = (pe: React.PointerEvent) => {
                        if (pe.button !== 0 || pe.metaKey || pe.ctrlKey) return;
                        pe.stopPropagation();
                        (timeline.current as HTMLElement).setPointerCapture(pe.pointerId);
                        const edge = (pe.target as HTMLElement).dataset.edge as 'l' | 'r' | undefined;
                        const kids = layer === 'ev' && (item as MarkupEvent).kind === 'реплика'
                          ? wordsOf(item.id).map((w) => ({ id: w.id, a: w.start, b: w.end })) : [];
                        evDrag.current = { id: item.id, layer, mode: edge ?? 'm', x0: pe.clientX, a: item.start, b: item.end, moved: false, kids };
                      };
                      if (layer === 'dlg') {
                        const d = item as DialogueMark;
                        if (d.kind === 'переход') {
                          const x = xOf(d.start);
                          if (x < -40 || x > W + 40) return null;
                          return (
                            <Tooltip key={d.id} content={`переход ${nameOf(d.from)} → ${nameOf(d.to)}${d.sub ? ` · ${d.sub}` : ''} ${fmt(d.start)}`}><div data-ev="1" onPointerDown={startDrag}
                              className="absolute cursor-grab" style={{ left: x - 5, width: 10, top: 1 + row * ROW_H, height: ROW_H }}>
                              <span className={`absolute inset-y-0 left-[4px] w-[2px] ${on ? 'bg-primary' : 'bg-emerald-600'}`} />
                              <span className={`absolute left-[8px] top-0 whitespace-nowrap text-[9px] leading-4 ${on ? 'font-semibold text-primary' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                {nameOf(d.from)}→{nameOf(d.to)}
                              </span>
                            </div></Tooltip>
                          );
                        }
                        const left = xOf(d.start); const width = Math.max(3, xOf(d.end) - left);
                        if (left > W || left + width < 0) return null;
                        const label = d.text || 'пауза'; const fits = label.length * 5.6 + 8 <= width;
                        return (
                          <Tooltip key={d.id} content={`пауза ${fmt(d.start)}–${fmt(d.end)}${d.text ? `\n${d.text}` : ''}`} className="whitespace-pre-line"><div data-ev="1" onPointerDown={startDrag}
                            className={`absolute cursor-grab overflow-hidden whitespace-nowrap rounded-[2px] px-1 text-[10px] italic leading-4 ${on ? 'bg-primary/35 font-semibold' : 'bg-emerald-500/20'}`}
                            style={{ left, width, top: 3 + row * ROW_H, height: ROW_H - 2 }}>
                            <span data-edge="l" className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize" />
                            {fits ? label : ''}
                            <span data-edge="r" className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize" />
                          </div></Tooltip>
                        );
                      }
                      const e = item as MarkupEvent;
                      const left = xOf(e.start); const width = Math.max(3, xOf(e.end) - left);
                      if (left > W || left + width < 0) return null;
                      const label = e.text || (KIND[e.kind]?.label ?? e.kind);
                      const fits = label.length * 5.6 + 8 <= width;
                      // Выбранное — насыщенной заливкой, без рамки: рамка и скругления путали (владелец 15.09).
                      return (
                        <Tooltip key={e.id} content={`${KIND[e.kind]?.label ?? e.kind}${e.speaker ? ` · ${nameOf(e.speaker)}` : ''}${e.end_kind ? ` · ${e.end_kind}` : ''} ${fmt(e.start)}–${fmt(e.end)}${e.text ? `\n${e.text}` : ''}${e.comment ? `\n${e.comment}` : ''}`} className="whitespace-pre-line"><div data-ev="1" onPointerDown={startDrag}
                          className={`absolute cursor-grab overflow-hidden whitespace-nowrap rounded-[2px] px-1 text-[10px] leading-4 ${on ? 'bg-primary/45 font-semibold text-primary-foreground' : KIND[e.kind]?.color ?? 'bg-rose-400/60'}`}
                          style={{ left, width, top: 3 + row * ROW_H, height: ROW_H - 2 }}>
                          <span data-edge="l" className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize" />
                          {fits ? label : ''}
                          <span data-edge="r" className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize" />
                        </div></Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {marquee && Math.abs(marquee.x1 - marquee.x0) > 2 && (
            <div className={`pointer-events-none absolute border ${marquee.create ? 'border-emerald-500 bg-emerald-500/15' : 'border-primary bg-primary/10'}`}
              style={{ left: HEAD_W + Math.min(marquee.x0, marquee.x1), width: Math.abs(marquee.x1 - marquee.x0), top: RULER_H, bottom: 0 }} />
          )}
          <div ref={cursorRef} className="pointer-events-none absolute inset-y-0 w-px bg-primary" style={{ left: HEAD_W }} />
        </div>
      </div>

      <div className="rounded border bg-card p-2 text-xs">
        {one ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{KIND[one.kind]?.label ?? one.kind} {fmt(one.start)}–{fmt(one.end)}</span>
              <Button size="sm" variant="outline" onClick={() => playRange(one.start, one.end)}><Play className="size-3" /> слушать</Button>
              {(['start', 'end'] as const).map((k) => (
                <span key={k} className="inline-flex items-center gap-0.5">
                  {k === 'start' ? 'начало' : 'конец'}
                  <button type="button" className="rounded-[2px] border px-1" onClick={() => patchEvent(one.id, { [k]: +Math.max(0, one[k] - 0.05).toFixed(3) })}>−</button>
                  <input className="w-16 rounded-[2px] border bg-background px-1" type="number" step={0.01} value={one[k]}
                    onChange={(e) => patchEvent(one.id, { [k]: Number(e.target.value) })} />
                  <button type="button" className="rounded-[2px] border px-1" onClick={() => patchEvent(one.id, { [k]: +(one[k] + 0.05).toFixed(3) })}>+</button>
                </span>
              ))}
              {one.kind === 'реплика' && !wordsOf(one.id).length && (one.text || '').trim() && (
                <Button size="sm" variant="outline" onClick={() => splitReply(one)}><Scissors className="size-3" /> разбить на слова</Button>
              )}
              {one.kind === 'реплика' && wordsOf(one.id).length > 0 && <span className="text-muted-foreground">слов: {wordsOf(one.id).length}</span>}
              {one.reply && <Button size="sm" variant="outline" onClick={() => setSel([one.reply as string])}>к реплике</Button>}
              <Button size="sm" variant="ghost" onClick={() => removeSelected([one.id])}><Trash2 className="size-3" /></Button>
            </div>
            {(['speech', 'bg', 'sound'] as Cat[]).map((c) => (
              <div key={c} className="flex flex-wrap items-center gap-1">
                <span className="w-12 text-muted-foreground">{CAT_LABEL[c]}</span>
                {KINDS.filter((k) => k.cat === c).map((k) => (
                  <button key={k.kind} type="button"
                    onClick={() => patchEvent(one.id, {
                      kind: k.kind,
                      speaker: c === 'speech' ? (one.speaker || speakers[0]?.id || '') : '',
                      reply: k.kind === 'слово' ? one.reply : '', end_kind: k.kind === 'реплика' ? one.end_kind : '',
                    })}
                    className={`rounded-[2px] px-1.5 py-0.5 ${one.kind === k.kind ? 'bg-primary/15 font-medium' : 'hover:bg-muted'}`}>
                    <span className={`mr-1 inline-block size-2 rounded-[1px] ${k.color}`} />{k.label}
                  </button>
                ))}
              </div>
            ))}
            {catOf(one.kind) === 'speech' && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="w-12 text-muted-foreground">говорит</span>
                {speakers.map((q) => (
                  <button key={q.id} type="button" onClick={() => patchEvent(one.id, { speaker: q.id })}
                    className={`rounded-[2px] px-1.5 py-0.5 ${one.speaker === q.id ? 'bg-primary/15 font-medium' : 'hover:bg-muted'}`}>{q.name}</button>
                ))}
              </div>
            )}
            {one.kind === 'реплика' && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-muted-foreground">как кончилась</span>
                {END_KINDS.map(([v, label]) => (
                  <button key={v} type="button" onClick={() => patchEvent(one.id, { end_kind: one.end_kind === v ? '' : v })}
                    className={`rounded-[2px] px-1.5 py-0.5 ${one.end_kind === v ? 'bg-primary/15 font-medium' : 'hover:bg-muted'}`}>{label}</button>
                ))}
              </div>
            )}
            {catOf(one.kind) === 'speech' && (
              <input className="w-full rounded-[2px] border bg-background px-2 py-1" placeholder="что сказано" value={one.text}
                onChange={(e) => patchEvent(one.id, { text: e.target.value })} />
            )}
            <input className="w-full rounded-[2px] border bg-background px-2 py-1" placeholder="комментарий к событию" value={one.comment}
              onChange={(e) => patchEvent(one.id, { comment: e.target.value })} />
          </div>
        ) : oneDlg ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{oneDlg.kind} {fmt(oneDlg.start)}{oneDlg.kind === 'пауза' ? `–${fmt(oneDlg.end)}` : ''}</span>
              {oneDlg.kind === 'пауза' && <Button size="sm" variant="outline" onClick={() => playRange(oneDlg.start, oneDlg.end)}><Play className="size-3" /> слушать</Button>}
              <Button size="sm" variant="ghost" onClick={() => removeSelected([oneDlg.id])}><Trash2 className="size-3" /></Button>
            </div>
            {oneDlg.kind === 'пауза' ? (
              <input className="w-full rounded-[2px] border bg-background px-2 py-1" placeholder="что происходит в эту паузу" value={oneDlg.text}
                onChange={(e) => patchDialogue(oneDlg.id, { text: e.target.value })} />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {(['from', 'to'] as const).map((k) => (
                  <label key={k} className="inline-flex items-center gap-1">
                    {k === 'from' ? 'от' : 'к'}
                    <Select className="h-7 rounded-[2px] py-0 pl-1 pr-7" wrapperClassName="w-auto" value={oneDlg[k]} onChange={(e) => patchDialogue(oneDlg.id, { [k]: e.target.value })}>
                      <option value="">—</option>
                      {speakers.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                    </Select>
                  </label>
                ))}
                {(['смена', 'перебивание'] as const).map((v) => (
                  <button key={v} type="button" onClick={() => patchDialogue(oneDlg.id, { sub: v })}
                    className={`rounded-[2px] px-1.5 py-0.5 ${oneDlg.sub === v ? 'bg-primary/15 font-medium' : 'hover:bg-muted'}`}>{v}</button>
                ))}
              </div>
            )}
          </div>
        ) : sel.length > 1 ? (
          <div className="flex flex-wrap items-center gap-2">
            выбрано: {sel.length}
            {canLink && <Button size="sm" variant="outline" onClick={linkWords}><Link2 className="size-3" /> сцепить в реплику</Button>}
            <Button size="sm" variant="ghost" onClick={() => removeSelected(sel)}><Trash2 className="size-3" /> удалить</Button>
          </div>
        ) : (
          <div className="text-muted-foreground">Выбери событие кликом — здесь его вид, спикер, текст и границы. Подсказки — под (?).</div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setSpeakerEdit(null); }}>
        {editing && (
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{editing.unknown ? 'Неопознанный спикер' : 'Спикер'}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <input className="w-full rounded-[2px] border bg-background px-2 py-1" value={editing.name}
                onChange={(e) => patchSpeaker(editing.id, { name: e.target.value })} />
              {([['пол', 'gender', GENDERS], ['возраст', 'age', AGES], ['роль', 'role', SPEAKER_ROLES]] as const).map(([label, key, opts]) => (
                <div key={key} className="flex flex-wrap items-center gap-1">
                  <span className="w-16 text-muted-foreground">{label}</span>
                  {opts.map(([v, text]) => (
                    <button key={v || 'none'} type="button" onClick={() => patchSpeaker(editing.id, { [key]: v })}
                      className={`rounded-[2px] px-1.5 py-0.5 text-xs ${editing[key] === v ? 'bg-primary/15 font-medium' : 'hover:bg-muted'}`}>{text}</button>
                  ))}
                </div>
              ))}
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={editing.unknown} onChange={(e) => patchSpeaker(editing.id, { unknown: e.target.checked })} />
                неопознанный (голос не удалось приписать)
              </label>
              <div className="space-y-1 border-t pt-3 text-xs">
                <div className="text-muted-foreground">
                  Удалить спикера — речи у него: {events.filter((e) => catOf(e.kind) === 'speech' && e.speaker === editing.id).length}
                </div>
                <div className="flex flex-wrap gap-1">
                  {speakers.filter((q) => q.id !== editing.id).map((q) => (
                    <Button key={q.id} size="sm" variant="outline" onClick={() => deleteSpeaker(editing.id, q.id)}>речь — к «{q.name}»</Button>
                  ))}
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteSpeaker(editing.id, null)}>
                    <Trash2 className="size-3" /> удалить вместе с речью
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
